import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';

import SEO from 'components/SEO/SEO';
import { useDavidApi } from 'utility/DavidApi/DavidApi';
import Container from 'components/Grid/Container';
import { cutStepDetails } from 'components/Layout/MapView/cutStepDetails';
import DataTable from 'components/Table/DataTable';
import { useReactToPrint } from 'react-to-print';
import { ReactComponent as Logo } from 'images/logo.svg';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal.js';
// import EditCutForm from 'components/Form/EditCutForm.js';
import DeleteProjectForm from 'components/Form/DeleteProjectForm';

import { DeleteButtonWrapper, YellowButtonWrapper } from 'components/Button/Button.styles';
import { CutReportBar, LogoWrapper, MobileViewNote, PrintButton, TableWrapper } from './CutList.styles.js';
import { getColorFromSlug } from 'utility/colors.js';
import useModal from "../components/Modal/useModal";
import { isAdmin } from "../utility/Authorization/roleUtils";
import FlexWraper from 'components/Grid/FlexWrapper.js';

const columns = [
    {
        name: "workOrder",
        label: "Work Order",
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({
                style: { borderLeft: "1px solid" }
            }),
        }
    },
    {
        name: "orderDate",
        label: "Order Date",
        options: {
            filter: true,
            sort: true,
            setCellProps: () => ({
                className: "text-center",
                style: { borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                className: "text-center",
            })
        }
    },
    {
        name: "originalMeasurements",
        label: "Original Measurements (in.)",
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({
                className: "text-center",
                style: { borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                className: "text-center",
            })
        }
    },
    {
        name: "address",
        label: "Street Number",
        options: {
            filter: true,
            sort: true,
            setCellProps: () => ({
                className: "text-center",
                style: { minWidth: "75px", borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                className: "text-center",
            })
        }
    },
    {
        name: "street",
        label: "Street",
        options: {
            filter: true,
            sort: true,
            setCellProps: () => ({
                style: { borderLeft: "1px solid" }
            }),
        }
    },
    {
        name: "latestStep",
        label: "Current Step",
        options: {
            filter: true,
            sort: true,
            setCellProps: () => ({
                style: { borderLeft: "1px solid"  }
            }),
        }
    },
    {
        name: "comments",
        label: "Comments",
        options: {
            filter: false,
            sort: false,
            setCellProps: () => ({
                style: { minWidth: "250px", borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                style: { minWidth: "250px" }
            })
        }
    },
    {
        name: "notes",
        label: "Notes",
        options: {
            filter: false,
            sort: false,
            setCellProps: () => ({
                style: { minWidth: "300px", height: "50px", borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                style: { minWidth: "275px",  }
            })
        }
    },
    {
        name: "actions",
        label: "Actions",
        options: {
            filter: false,
            sort: false,
            setCellProps: () => ({
                className: "print-hidden",
                style: { minWidth: "100px", borderLeft: "1px solid" }
            }),
            setCellHeaderProps: () => ({
                className: "print-hidden",
                style: { minWidth: "100px" }
            })
        }
    },
];


function CutList() {
    const api = useDavidApi();
    const [cuts, setCuts] = useState(null);
    const [loading, setLoading] = useState(true);
    // const editCutModal = useModal();
    // const [editCutModalCut, setEditCutModalCut] = useState(null);
    const deleteProjectModal = useModal();
    const [deleteProjectModalClient, setDeleteProjectModalClient] = useState(null);

    const deleteProjectModalTriggerHandler = (id) => {
        setDeleteProjectModalClient(id);
        deleteProjectModal.show();
    }

    const initialState = {
        incomplete: true
    }

    // const modalRef = useRef(null);
    // const scrollTop = () => {
    //     modalRef.current.scrollIntoView();
    // }

    const currentDate = new Date().toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const tableRef = useRef();
    const reactToPrint = useReactToPrint({
        contentRef: tableRef,
        documentTitle: "CutReport-" + currentDate
    });

    useEffect(() => {
        setLoading(true);
        refreshCuts();
    }, []);

    useEffect(() => {
        if (cuts === null) {
            return;
        }

        setLoading(false);
    }, [cuts]);

    // const editCutModalTriggerHandler = (cutData) => {
    //     setEditCutModalCut(cutData);
    //     editCutModal.show();
    // }

    const getTableRowDataForCut = (cut) => {
        let formattedDate = null;
        if (cut?.project?.orderDate) {
            formattedDate = new Date(cut.project.orderDate).toLocaleDateString('en-us', {
                timeZone: 'UTC',
                year: "2-digit",
                month: "2-digit",
                day: "2-digit",
            });
        }

        let workOrder = null;
        if (cut?.project?.projectNumber) {
            workOrder = cut?.project?.projectNumber;
        }

        let latestStep = '';
        if (cut?.steps?.length === 0){
            const step = cutStepDetails[0];
            latestStep = step?.label;
        } else if (cut.steps.length === 1) {
            const step = cutStepDetails[1];
            latestStep = step?.label;
        } else if (cut.steps.length === 2) {
            const step = cutStepDetails[2];
            latestStep = step?.label;
        } else if (cut.steps.length === 3) {
            const step = cutStepDetails[3];
            latestStep = step?.label;
        } else if (cut.steps.length === 4) {
            const step = cutStepDetails[4];
            latestStep = step?.label;
        } else {
            const step = cutStepDetails[5];
            latestStep = step?.label;
        }

        let originalMeasurements = '';
        let originalLength = '';
        let originalWidth = '';
        let originalDepth = '';
        if (cut.originalLength) {
            originalLength = cut.originalLength;
            originalMeasurements= cut.originalLength + 'L x ' + cut.originalWidth + 'W x ' + cut.originalDepth + 'D';
        } else {
            originalLength = '0';
        }

        if (cut.originalWidth) {
            originalWidth = cut.originalWidth;
        } else {
            originalWidth = '0';
        }

        if (cut.originalDepth) {
            originalDepth = cut.originalDepth;
        } else {
            originalDepth = '0';
        }

        if (originalLength && originalWidth && originalDepth) {
            originalMeasurements = originalLength + 'L x ' + originalWidth + 'W x ' + originalDepth + 'D';
        }

        if (originalLength === '0' && originalWidth === '0' && originalDepth === '0' ) {
            originalMeasurements = '';
        }

        let cutTableRowData = {
            ...cut,
            orderDate: formattedDate,
            workOrder: workOrder,
            latestStep: latestStep,
            originalMeasurements: originalMeasurements,
            actions: (
                <>
                    {isAdmin(api.profile) && (
                        <div className="table-row-actions-container">
                            <FlexWraper>
                                <Button
                                    size={'sm'}
                                    iconLeft={<FontAwesomeIcon icon={solid('edit')} />}
                                    to={"/projects/" + cut?.project?.id + "/edit"}
                                    target="_blank"
                                >
                                    Edit
                                </Button>
                                <Button
                                    size={'sm'}
                                    iconLeft={<FontAwesomeIcon icon={solid('trash')} />}
                                    onClick={() => deleteProjectModalTriggerHandler(cut?.project?.id)}
                                    variant="red"
                                >
                                    Delete
                                </Button>
                            </FlexWraper>
                        </div>
                    )} 
                </>
            )
        };

        return cutTableRowData;
    }

    const refreshCuts = () => {
        (async () => {
            let response = await api.searchAllCuts(initialState);

            const filteredCuts = response.filter(cut => {
                const cutDate = new Date(cut?.project?.orderDate);
                const oneYearAgo = new Date();
                oneYearAgo.setDate(oneYearAgo.getDate() - 365);

                const olderThanOneYear = cutDate < oneYearAgo;

                if (olderThanOneYear) {
                    return false;
                } else {
                    return true;
                }

            })

            setCuts(filteredCuts);
            setLoading(false);
        })();
    }

    return (
        <>
            <SEO title="Cut List" />
            <Container vpadding={30} fluid>
                <h1>Cut Report</h1>
                <TableWrapper ref={tableRef}>
                    {!loading && (
                        <CutReportBar>
                            <div>
                                <h4 style={{marginBottom: '15px'}}>Cut data as of {currentDate}</h4>
                                <MobileViewNote><em>Note: This page has not been optimized for mobile view.</em></MobileViewNote>
                            </div>
                            <div>
                                <YellowButtonWrapper>
                                    <PrintButton onClick={reactToPrint}>Print Report</PrintButton>
                                </YellowButtonWrapper>
                            </div>
                            <LogoWrapper>
                                <Logo style={{ fill: getColorFromSlug('david-black') }} />
                            </LogoWrapper>
                        </CutReportBar>
                    )}
                    <DataTable
                        columns={columns}
                        data={Array.isArray(cuts) ? cuts.map(getTableRowDataForCut) : []}
                        loading={loading}
                        options={{
                            search: false,
                            filter: false,
                            rowsPerPage: 500,
                            rowsPerPageOptions: [100, 250, 500, 1000, 2000],
                        }}
                    />
                    {/* <Modal title="Edit Cut" isShowing={editCutModal.isShowing} hide={editCutModal.hide} modalRef={modalRef}>
                        <EditCutForm
                            cut={editCutModalCut}
                            latestStep={editCutModalCut?.latestStep}
                            formattedDate={editCutModalCut?.orderDate}
                            hideModal={editCutModal.hide}
                            refreshCuts={refreshCuts}
                            setLoading={setLoading}
                            toggleModal={editCutModal.hide}
                            scrollTop={scrollTop}
                        />
                    </Modal> */}
                </TableWrapper>
            </Container>

            <Modal title="Delete Project" isShowing={deleteProjectModal.isShowing} hide={deleteProjectModal.hide}>
                <DeleteProjectForm
                    id={deleteProjectModalClient}
                    hideModal={deleteProjectModal.hide}
                    onDelete={refreshCuts}
                />
            </Modal>
        </>
    )
}

export default CutList;
